import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import Header from '../../components/Header';
import CrisisOverlay from '../../components/CrisisOverlay';
import LanternLoader from '../../components/LanternLoader';
import UpgradeModal from '../../components/UpgradeModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';

const MAX_WORDS = 3000;

// Auto-generate 2-word title from user input
const generateTitle = (userInput) => {
  if (!userInput) return 'Reflection';
  const input = userInput.toLowerCase();
  
  const themes = [
    [['grief', 'death', 'loss', 'died', 'passing', 'mourning'], 'Grief'],
    [['anxiety', 'anxious', 'nervous', 'panic', 'worry', 'worried'], 'Anxiety'],
    [['depression', 'depressed', 'hopeless', 'empty'], 'Depression'],
    [['anger', 'angry', 'furious', 'rage', 'mad', 'frustrated', 'hate', 'hatred'], 'Anger'],
    [['forgive', 'forgiveness', 'grudge', 'betrayed'], 'Forgiveness'],
    [['fear', 'afraid', 'scared', 'terrified'], 'Fear'],
    [['marriage', 'spouse', 'husband', 'wife', 'divorce'], 'Marriage'],
    [['family', 'parent', 'child', 'kids', 'son', 'daughter', 'mother', 'father', 'mom', 'dad'], 'Family'],
    [['work', 'job', 'career', 'boss', 'coworker', 'office', 'fired'], 'Work'],
    [['money', 'financial', 'debt', 'bills', 'broke', 'budget'], 'Financial'],
    [['health', 'sick', 'illness', 'disease', 'cancer', 'hospital'], 'Health'],
    [['faith', 'believe', 'god', 'prayer', 'spiritual', 'church', 'jesus'], 'Faith'],
    [['relationship', 'friend', 'lonely', 'alone'], 'Relationship'],
    [['love', 'dating', 'heartbreak', 'breakup'], 'Love'],
    [['purpose', 'meaning', 'direction', 'calling'], 'Purpose'],
    [['decision', 'choice', 'confused', 'uncertain'], 'Decision'],
    [['addiction', 'drinking', 'alcohol', 'drugs'], 'Addiction'],
    [['leadership', 'lead', 'leader', 'team'], 'Leadership'],
    [['peace', 'calm', 'rest', 'overwhelmed', 'stress'], 'Peace'],
    [['politics', 'trump', 'vance', 'biden', 'election', 'government'], 'Politics'],
    [['food', 'eat', 'eating', 'diet', 'corn', 'bread'], 'Food'],
  ];
  
  const states = [
    [['struggle', 'struggling', 'hard', 'difficult', 'challenge'], 'Struggle'],
    [['journey', 'path', 'process', 'walk'], 'Journey'],
    [['crisis', 'emergency', 'urgent', 'desperate'], 'Crisis'],
    [['healing', 'heal', 'recover', 'recovery'], 'Healing'],
    [['waiting', 'wait', 'patience', 'patient'], 'Waiting'],
    [['transition', 'moving', 'change', 'new', 'season'], 'Transition'],
    [['growth', 'grow', 'growing', 'learning'], 'Growth'],
    [['burden', 'heavy', 'weight', 'tired', 'exhausted'], 'Burden'],
    [['hope', 'hopeful', 'future', 'better'], 'Hope'],
    [['doubt', 'uncertain', 'questioning'], 'Doubt'],
    [['victory', 'overcome', 'won', 'success'], 'Victory'],
    [['lost', 'confused', 'searching'], 'Search'],
    [['think', 'thinking', 'thought', 'thoughts'], 'Thoughts'],
    [['evil', 'bad', 'wrong', 'hate'], 'Concern'],
  ];
  
  let theme = 'Life';
  for (const [keywords, name] of themes) {
    if (keywords.some(kw => input.includes(kw))) { theme = name; break; }
  }
  
  let state = 'Moment';
  for (const [keywords, name] of states) {
    if (keywords.some(kw => input.includes(kw))) { state = name; break; }
  }
  
  return `${theme} ${state}`;
};

const MainDashboard = () => {
  const navigate = useNavigate();
  const { user, isPremium, isAdmin, isTrialing, canAccessApp, refreshUser } = useAuth();
  
  // 🔒 IRONCLAD BOUNCER: Block 'incomplete' users BEFORE any render
  // This is an EARLY RETURN - component never renders for incomplete users
  if (!user || user.subscription_status === 'incomplete') {
    return <Navigate to="/subscription-plans" replace />;
  }

  // Determine if user is an active subscriber (can generate reflections)
  const isActiveSubscriber = isAdmin || canAccessApp;

  const { language, detectLanguage, t, isSpanish } = useLanguage();
  const [showCrisisOverlay, setShowCrisisOverlay] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [recentReflections, setRecentReflections] = useState([]);
  const [settings, setSettings] = useState({});
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  
  // Handle Upgrade Now - SELF-HEALING BILLING BRIDGE
  // Trial users: Checkout with card pre-filled + coupon field
  // Others: Portal or fresh checkout
  const handleUpgradeNow = async () => {
    setUpgradeLoading(true);
    try {
      const result = await api.smartUpgrade(language);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Error with upgrade:', error);
      setError(isSpanish 
        ? 'No se puede procesar. Intenta de nuevo.'
        : 'Unable to process. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };
  
  // Use ref for input to prevent iPhone focus issues with controlled components
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Sync input value for word count calculation
  const updateWordCount = useCallback((text) => {
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
    
    // Auto-detect language from input
    if (text.length > 10) {
      detectLanguage(text);
    }
  }, [detectLanguage]);

  // Handle input change - update word count without losing focus
  const handleInputChange = useCallback((e) => {
    const text = e.target.value;
    setInputValue(text);
    updateWordCount(text);
  }, [updateWordCount]);

  const loadData = async () => {
    try {
      const [historyData, settingsData] = await Promise.all([
        api.getReflectionHistory(5, 0),
        api.getPublicSettings()
      ]);
      setRecentReflections(historyData.reflections || []);
      setSettings(settingsData);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  // Check for crisis keywords
  const detectCrisis = (text) => {
    const crisisKeywords = [
      // English
      'suicide', 'kill myself', 'end my life', 'want to die', 'self-harm',
      'hurt myself', 'cutting', 'overdose', 'no reason to live', 'better off dead',
      'suicidal', 'harm myself', 'ending it all', '988', '911',
      // Spanish
      'suicidio', 'matarme', 'terminar mi vida', 'quiero morir', 'autolesión',
      'hacerme daño', 'cortarme', 'sobredosis', 'sin razón para vivir'
    ];
    return crisisKeywords.some(keyword => text?.toLowerCase()?.includes(keyword));
  };

  const handleReflectionSubmit = async (e) => {
    e.preventDefault();
    
    // Get current value from ref
    const userInput = inputRef.current?.value || inputValue;
    
    if (!userInput.trim()) return;

    // Check word count limit
    if (wordCount > MAX_WORDS) {
      setError(t('wordLimitError'));
      return;
    }

    // Check for crisis keywords first
    if (detectCrisis(userInput)) {
      setShowCrisisOverlay(true);
      return;
    }

    // ADMIN GOLDEN KEY: Skip ALL limit checks for admins - unlimited access
    // Check usage limit based on user type
    const usage = user?.usage;
    const limitType = usage?.limit_type || 'total';
    
    if (!isAdmin && usage && usage.remaining <= 0) {
      // Users who've hit daily limit
      if (isTrialing) {
        // Hard lock for trial users - show upgrade modal
        setShowUpgradeModal(true);
        return;
      } else if (isPremium) {
        // Premium users just hit daily limit
        setError(isSpanish 
          ? '¡Límite diario de 7 reflexiones alcanzado. Vuelve mañana!'
          : 'Daily limit of 7 reflections reached. Try again tomorrow!');
        return;
      }
    }

    // Show warning if low on usage (last reflection) - only for non-admins
    if (!isAdmin && usage && usage.remaining === 1) {
      setShowLimitWarning(true);
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const reflectionData = await api.generateReflection(userInput, language);

      if (reflectionData.is_crisis) {
        setShowCrisisOverlay(true);
        setIsSubmitting(false);
        return;
      }

      // Refresh user to update usage
      await refreshUser();

      // Navigate to response page with user input included
      navigate('/reflection-response', {
        state: { 
          reflectionData: {
            ...reflectionData,
            user_input: userInput // Ensure user input is passed
          }
        }
      });
    } catch (err) {
      console.error('Error generating reflection:', err);
      if (err.message?.includes('Trial limit') || err.message?.includes('Límite de prueba')) {
        // Trial users exhausted their 5 total reflections
        setShowUpgradeModal(true);
      } else if (err.message?.includes('Daily limit') || err.message?.includes('Límite')) {
        // Users hit daily limit
        if (!isAdmin) {
          setError(isSpanish 
            ? '¡Límite diario de 7 reflexiones alcanzado. Vuelve mañana!'
            : 'Daily limit of 7 reflections reached. Try again tomorrow!');
        }
      } else if (err.message?.includes('3,000 words') || err.message?.includes('3000')) {
        setError(t('wordLimitError'));
      } else {
        setError(err.message || (isSpanish 
          ? 'No se pudo generar la reflexión. Por favor intenta de nuevo.'
          : 'Unable to generate reflection. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const usage = user?.usage || { used: 0, limit: 5, remaining: 5, limit_type: 'daily' };
  const limitType = usage.limit_type || 'daily';
  const isHardLocked = (isTrialing || isPremium) && usage.remaining <= 0;
  const firstName = user?.name?.split(' ')?.[0] || (isSpanish ? 'Amigo' : 'Friend');

  // Show full-screen lantern loader when submitting
  if (isSubmitting) {
    return (
      <div className="min-h-dvh bg-background">
        <Header />
        <main className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-2xl mx-auto">
            <div className="bento-item flex flex-col items-center justify-center py-16">
              <LanternLoader 
                message={t('receiving')}
                size="large" 
              />
              <p className="text-sm text-muted-foreground mt-6 text-center max-w-md">
                {isSpanish 
                  ? 'Estamos preparando guía fundamentada en las Escrituras solo para ti. Esto puede tomar un momento...'
                  : "We're preparing scripture-grounded guidance just for you. This may take a moment..."}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background">
      <Header />
      <CrisisOverlay
        isVisible={showCrisisOverlay}
        onClose={() => setShowCrisisOverlay(false)}
      />
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        isSpanish={isSpanish}
      />

      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="font-heading font-bold text-3xl lg:text-4xl text-foreground mb-2">
              {t('welcomeBack')}, <span className="text-gold-gradient">{firstName}</span>
            </h1>
            <p className="text-muted-foreground">
              {t('shareYourHeart')}
            </p>
          </div>

          {/* Low Light Warning */}
          {showLimitWarning && !isPremium && (
            <div className="mb-6 p-4 glass border border-warning/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Icon name="AlertTriangle" size={20} color="#FFD700" />
                <div className="flex-1">
                  <p className="text-sm text-foreground">
                    <strong>{isSpanish ? '¡Poca luz!' : 'Low on light!'}</strong> {isSpanish 
                      ? `Te queda ${usage.remaining} reflexión hoy.`
                      : `You have ${usage.remaining} reflection remaining today.`}
                  </p>
                </div>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => navigate('/subscription-plans')}
                >
                  {t('upgrade')}
                </Button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="#EF4444" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-error">{error}</p>
                  {(error.includes('limit') || error.includes('Límite')) && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => navigate('/subscription-plans')}
                      className="mt-2"
                    >
                      {isSpanish ? 'Ver Planes Premium' : 'View Premium Plans'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bento Grid Layout */}
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Main Input Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Reflection Input Card */}
              <div className="bento-item">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Icon name="Feather" size={24} color="#FFD700" />
                  </div>
                  <div>
                    <h2 className="font-heading font-semibold text-xl text-foreground">
                      {t('whatsOnYourHeart')}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {t('shareThoughts')}
                    </p>
                  </div>
                </div>

                {/* PAYWALL CTA: Show upgrade prompt for inactive subscribers */}
                {!isActiveSubscriber ? (
                  <div className="text-center py-8 px-4" data-testid="paywall-cta">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon name="Lock" size={28} color="#FFD700" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                      {isSpanish ? 'Tu acceso ha expirado' : 'Your access has expired'}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
                      {isSpanish 
                        ? 'Tus reflexiones guardadas siguen aquí. Reanuda tu camino para generar nuevas reflexiones.'
                        : 'Your saved reflections are still here. Resume your journey to generate new reflections.'}
                    </p>
                    <Button
                      variant="default"
                      className="gold-glow px-6 py-3"
                      onClick={() => navigate('/subscription-plans')}
                      data-testid="paywall-upgrade-btn"
                    >
                      <Icon name="Sparkles" size={18} className="mr-2" />
                      {isSpanish 
                        ? 'Reanuda tu camino por $6.99/mes'
                        : 'Resume your journey for $6.99/mo'}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-3">
                      {isSpanish 
                        ? 'Puedes ver tus reflexiones anteriores abajo'
                        : 'You can still view your previous reflections below'}
                    </p>
                  </div>
                ) : (
                <form onSubmit={handleReflectionSubmit}>
                  <div className="relative">
                    {/* Mobile-optimized textarea - uses native keyboard with mic */}
                    <textarea
                      ref={inputRef}
                      defaultValue=""
                      onChange={handleInputChange}
                      placeholder={t('tapToType')}
                      rows={5}
                      className="w-full p-4 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-base min-h-[140px]"
                      disabled={isSubmitting}
                      data-testid="reflection-input"
                      autoComplete="off"
                      autoCorrect="on"
                      spellCheck="true"
                    />
                  </div>

                  {/* Word count indicator */}
                  <div className="flex items-center justify-between mt-2">
                    <p className={`text-xs ${wordCount > MAX_WORDS ? 'text-error' : 'text-muted-foreground'}`}>
                      {wordCount} / {MAX_WORDS} {t('words')}
                      {wordCount > MAX_WORDS && (
                        <span className="ml-2 text-error font-medium">
                          ({t('limitExceeded')})
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex items-center justify-end mt-4">
                    <Button
                      type="submit"
                      variant="default"
                      disabled={isSubmitting || !inputValue.trim() || wordCount > MAX_WORDS}
                      className="gold-glow"
                      data-testid="submit-reflection"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                          {t('receiving')}
                        </>
                      ) : (
                        <>
                          <Icon name="Sparkles" size={18} className="mr-2" />
                          {t('receiveLight')}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
                )}
              </div>

              {/* Recent Reflections */}
              <div className="bento-item">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Icon name="Gem" size={20} color="#FFD700" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground">
                      {t('recentGold')}
                    </h3>
                  </div>
                  <button
                    onClick={() => navigate('/history')}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    {t('viewAll')}
                    <Icon name="ArrowRight" size={14} />
                  </button>
                </div>

                {recentReflections.length > 0 ? (
                  <div className="space-y-3">
                    {recentReflections.slice(0, 3).map((reflection) => (
                      <button
                        key={reflection.id}
                        onClick={() => navigate('/reflection-response', { state: { reflectionData: reflection } })}
                        className="w-full text-left p-4 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                        data-testid={`reflection-item-${reflection.id}`}
                      >
                        {/* Title - Auto-generated 2-word summary */}
                        <h4 className="font-semibold text-foreground mb-1">
                          {reflection.title || generateTitle(reflection.user_input)}
                        </h4>
                        {/* Date - Secondary below title */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                          <Icon name="Calendar" size={12} />
                          {new Date(reflection.created_at).toLocaleDateString()}
                          {reflection.is_saved && (
                            <span className="flex items-center gap-1 text-primary">
                              <Icon name="Bookmark" size={12} />
                              {t('saved')}
                            </span>
                          )}
                        </div>
                        {/* Preview text */}
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {reflection.user_input}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="Inbox" size={40} color="#94A3B8" className="mx-auto mb-3" />
                    <p className="text-muted-foreground">{t('noReflections')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('startJourney')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar Column */}
            <div className="space-y-6">
              {/* Usage Card */}
              <div className="bento-item">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    {isPremium ? (
                      <Icon name="Crown" size={20} color="#FFD700" />
                    ) : isTrialing ? (
                      <Icon name="Gift" size={20} color="#FFD700" />
                    ) : (
                      <Icon name="Sparkles" size={20} color="#FFD700" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">
                      {isPremium 
                        ? t('premiumMember')
                        : isTrialing
                          ? t('premiumMember')
                          : t('dailyLight')}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {isPremium || isTrialing
                        ? `7 ${t('dailyReflections')}`
                        : t('subscribeToAccess')}
                    </p>
                  </div>
                </div>

                {/* Trial Usage Display */}
                {isTrialing && (
                  <>
                    {/* Trial Days Remaining Badge - Clickable with Upgrade Now */}
                    {user?.trial_info && (
                      <div 
                        className="mb-4 p-3 rounded-lg bg-primary/10 border border-primary/30 cursor-pointer hover:bg-primary/20 transition-colors"
                        onClick={handleUpgradeNow}
                        data-testid="trial-badge-upgrade"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon name="Gift" size={18} color="#FFD700" />
                            <span className="text-sm font-medium text-primary">
                              {isSpanish 
                                ? `Prueba Gratuita: ${user.trial_info.days_remaining} ${user.trial_info.days_remaining === 1 ? 'día' : 'días'} restantes`
                                : `Free Trial: ${user.trial_info.days_remaining} ${user.trial_info.days_remaining === 1 ? 'day' : 'days'} remaining`}
                            </span>
                          </div>
                          <button 
                            className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                            disabled={upgradeLoading}
                          >
                            {upgradeLoading ? (
                              <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <Icon name="Zap" size={14} color="#FFD700" />
                                {isSpanish ? 'Mejorar Ahora' : 'Upgrade Now'}
                              </>
                            )}
                          </button>
                        </div>
                        {user.trial_info.end_date && (
                          <p className="text-xs text-muted-foreground mt-1 ml-6">
                            {isSpanish ? `Termina: ${user.trial_info.end_date}` : `Ends: ${user.trial_info.end_date}`}
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">
                          {t('dailyReflectionsUsed')}
                        </span>
                        <span className={`font-medium ${isHardLocked ? 'text-amber-400' : 'text-foreground'}`}>
                          {usage.used} / {usage.limit}
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isHardLocked ? 'bg-amber-500' : usage.remaining <= 1 ? 'bg-amber-400' : 'bg-primary'
                          }`}
                          style={{ width: `${Math.min((usage.used / usage.limit) * 100, 100)}%` }}
                        />
                      </div>
                      {isHardLocked && (
                        <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                          <Icon name="Lock" size={12} />
                          {isSpanish 
                            ? `Límite diario alcanzado. Se reinicia: ${usage.next_reset_display || 'mañana a medianoche'}`
                            : `Daily limit reached. Resets: ${usage.next_reset_display || 'tomorrow at midnight'}`}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Premium Usage Display */}
                {isPremium && (
                  <>
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">
                          {t('reflectionsToday')}
                        </span>
                        <span className="text-foreground font-medium">
                          {usage.used} / {usage.limit}
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((usage.used / usage.limit) * 100, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {`${usage.remaining} ${t('dailyReflectionsRemaining')}`}
                      </p>
                      {/* Counter Reset Time - Dynamic based on user's timezone */}
                      <p className="text-xs text-primary mt-1">
                        {isSpanish 
                          ? `Se reinicia: ${usage.next_reset_display || 'mañana a medianoche'}`
                          : `Resets: ${usage.next_reset_display || 'tomorrow at midnight'}`}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <Icon name="Check" size={14} color="#10B981" />
                        {`7 ${t('dailyReflections')}`}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <Icon name="Check" size={14} color="#10B981" />
                        {t('fullPipeline')}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <Icon name="Check" size={14} color="#10B981" />
                        {t('fieldNotesJournaling')}
                      </div>
                      {user?.subscription_end_date && (
                        <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                          {isSpanish ? 'Suscripción renueva' : 'Subscription renews'}: {new Date(user.subscription_end_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Quick Actions */}
              <div className="bento-item">
                <h3 className="font-heading font-semibold text-foreground mb-4">
                  {t('quickActions')}
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate('/history')}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                    data-testid="go-to-history"
                  >
                    <Icon name="Gem" size={18} color="#FFD700" />
                    <span className="text-sm text-foreground">{t('myGoldMine')}</span>
                  </button>
                  <button
                    onClick={() => navigate('/account-settings')}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  >
                    <Icon name="Settings" size={18} color="#94A3B8" />
                    <span className="text-sm text-foreground">{t('settings')}</span>
                  </button>
                </div>
              </div>

              {/* Privacy Card */}
              <div className="bento-item">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="Shield" size={20} color="#10B981" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      {t('yourPrivacyMatters')}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t('privacyDescription')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm text-muted-foreground">
            © 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MainDashboard;
